"use client";

import { useState, useEffect } from "react";
import { FaRegFolder } from "react-icons/fa";
import { MdKeyboardArrowDown } from "react-icons/md";
import categoryAndGroupAPI from "../../services/categoryAndGroup/categoryAndGroupAPI";
import mainAPI from "../../services/main/mainAPI";
import sequenceAPI from "../../services/sequence/sequenceAPI";
import StatusPopUp from "./statusPopUp";
import ConfirmPopUp from "./confirmPopUp";

type CreatePopUpProps = {
    title: string;
    onClose: () => void;
    onConfirm: (createdGame: { id: number | string; exercise_name?: string; uuid?: string; description?: string }) => void;
};

function getFileExtension(fileName: string) {
    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex < 0 || dotIndex === fileName.length - 1) {
        return "";
    }
    return fileName.slice(dotIndex);
}

function getTeacherUuidFromLocalStorage() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return undefined;

        const parsed = JSON.parse(raw) as { id?: string };
        if (!parsed || typeof parsed !== "object") return undefined;
        if (!parsed.id || typeof parsed.id !== "string") return undefined;

        return parsed.id;
    } catch {
        return undefined;
    }
}

function getUserRoleFromLocalStorage() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return "";

        const parsed = JSON.parse(raw) as { role?: string };
        if (!parsed || typeof parsed !== "object") return "";

        return typeof parsed.role === "string" ? parsed.role.trim() : "";
    } catch {
        return "";
    }
}

export default function CreateGamePopUp({ title, onClose, onConfirm }: CreatePopUpProps) {
    const [fileName, setFileName] = useState<string>("");
    const [exerciseName, setExerciseName] = useState<string>("");
    const [createBy, setCreateBy] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [selectedGrade, setSelectedGrade] = useState<string>("");
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);
    const [groups, setGroups] = useState<Array<{ id: number; name: string }>>([]);
    const [selectedCategoryName, setSelectedCategoryName] = useState<string>("");
    const [selectedGroupName, setSelectedGroupName] = useState<string>("");
    const [statusPopUp, setStatusPopUp] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);
    const [createdGameData, setCreatedGameData] = useState<{ id: number | string; exercise_name?: string; uuid?: string; description?: string } | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isConfirmCreateOpen, setIsConfirmCreateOpen] = useState(false);

    const gameTypeByTitle: Record<string, string> = {
        "Find the match": "find_the_match",
        "Game show Quiz": "game_show_quiz",
        "Flip cards": "flip_cards",
        "Air plan Flying Fruits": "air_plan/flying_fruits",
        "Flappy Bird": "flappy_bird",
        "Whack a mole": "whack_a_mole",
        "ตีตุ่น": "whack_a_mole",
        "Anagram": "anagram",
        "Complete the Sentence": "complete_the_sentence",
        "Complete the sentence": "complete_the_sentence",
        "Situation": "situation",
    };

    // useEffect(() => {
    //     let mounted = true;
    //     const teacherUuid = getTeacherUuidFromLocalStorage();

    //     if (!teacherUuid) {
    //         if (mounted) setCategories([]);
    //     } else {
    //         categoryAndGroupAPI
    //             .getCategoriesByTeacherUuid(teacherUuid)
    //             .then((res: any) => {
    //                 const data = Array.isArray(res) ? res : res?.data ?? [];
    //                 if (mounted) setCategories(Array.isArray(data) ? data : []);
    //             })
    //             .catch(() => {
    //                 if (mounted) setCategories([]);
    //             });
    //     }

    //     if (!teacherUuid) {
    //         if (mounted) setGroups([]);
    //     } else {
    //         categoryAndGroupAPI
    //             .getGroupsByTeacherUuid(teacherUuid)
    //             .then((res: any) => {
    //                 const data = Array.isArray(res) ? res : res?.data ?? [];
    //                 if (mounted) setGroups(Array.isArray(data) ? data : []);
    //             })
    //             .catch(() => {
    //                 if (mounted) setGroups([]);
    //             });
    //     }
    //     return () => {
    //         mounted = false;
    //     };
    // }, []);

    useEffect(() => {
        let mounted = true;
        const teacherUuid = getTeacherUuidFromLocalStorage();

        categoryAndGroupAPI
            .getAllCategories()
            .then((res: any) => {
                const data = Array.isArray(res) ? res : res?.data ?? [];
                if (mounted) setCategories(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (mounted) setCategories([]);
            });

        categoryAndGroupAPI
            .getGroupsByTeacherUuid(teacherUuid)
            .then((res: any) => {
                const data = Array.isArray(res) ? res : res?.data ?? [];
                if (mounted) setGroups(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (mounted) setGroups([]);
            });

        return () => {
            mounted = false;
        };
    }, []);

    const showValidationError = (fieldLabel: string) => {
        setStatusPopUp({
            title: "แจ้งเตือน",
            message: `กรุณา${fieldLabel}`,
            type: "info",
        });
    };

    const handleSubmitCreateGame = async () => {
        if (!exerciseName.trim()) {
            showValidationError("กรอกชื่อ");
            return;
        }

        if (!selectedCategoryName) {
            showValidationError("เลือกหมวดหมู่");
            return;
        }

        if (!selectedGroupName) {
            showValidationError("เลือกกลุ่ม");
            return;
        }

        if (!selectedGrade) {
            showValidationError("เลือกชั้น");
            return;
        }

        if (!thumbnailFile) {
            showValidationError("แนบ File ภาพ");
            return;
        }

        const teacherUuid = getTeacherUuidFromLocalStorage();
        const isSuperAdmin = getUserRoleFromLocalStorage() === "super_admin";

        try {
            setIsSubmitting(true);

            const isSequence = title === "Sequence";

            if (!isSequence) {
                const gameType = gameTypeByTitle[title];
                if (!gameType) {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "ไม่พบประเภทเกมของเทมเพลตนี้",
                        type: "error",
                    });
                    setIsSubmitting(false);
                    return;
                }

                const thumbnailExtension = getFileExtension(thumbnailFile.name);
                const thumbnailUploadFileName = `thumbnail_${Date.now()}${thumbnailExtension}`;

                const response = await mainAPI.createGame({
                    create_by: createBy.trim(),
                    exercise_name: exerciseName.trim(),
                    description: description.trim(),
                    subject: selectedCategoryName,
                    group_id: groups.find((g) => g.name === selectedGroupName)?.id,
                    teacher_uuid: teacherUuid,
                    question_type: "single_question",
                    game_type: gameType,
                    thumbnail: thumbnailFile,
                    thumbnailFileName: thumbnailUploadFileName,
                    game_default: isSuperAdmin,
                });

                if (response?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "สร้างเกมไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
                        type: "error",
                    });
                    return;
                }

                setStatusPopUp({
                    title: "สำเร็จ",
                    message: "สร้างเกมสำเร็จ",
                    type: "success",
                });
                setCreatedGameData({
                    id: response?.id,
                    exercise_name: response?.exercise_name,
                    uuid: response?.uuid,
                    description: description.trim(),
                });
            } else {
                const thumbnailExtension = getFileExtension(thumbnailFile.name);
                const thumbnailUploadFileName = `thumbnail_${Date.now()}${thumbnailExtension}`;

                const response = await sequenceAPI.createSequence({
                    create_by: createBy.trim(),
                    exercise_name: exerciseName.trim(),
                    description: description.trim(),
                    subject: selectedCategoryName,
                    group_id: groups.find((g) => g.name === selectedGroupName)?.id,
                    teacher_uuid: teacherUuid,
                    thumbnail: thumbnailFile,
                    thumbnailFileName: thumbnailUploadFileName,
                    sequence_default: isSuperAdmin,
                });

                if (response?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "สร้าง Sequence ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
                        type: "error",
                    });
                    return;
                }

                setStatusPopUp({
                    title: "สำเร็จ",
                    message: "สร้าง Sequence สำเร็จ",
                    type: "success",
                });
                setCreatedGameData({
                    id: response?.id,
                    exercise_name: response?.exercise_name,
                    uuid: response?.uuid,
                    description: description.trim(),
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseStatus = () => {
        const isSuccess = statusPopUp?.type === "success";
        setStatusPopUp(null);

        if (isSuccess && createdGameData?.id) {
            onConfirm(createdGameData);
        }
    };

    const handleClickCreateButton = () => {
        if (isSubmitting) {
            return;
        }
        setIsConfirmCreateOpen(true);
    };

    const handleCancelConfirmCreate = () => {
        setIsConfirmCreateOpen(false);
    };

    const handleConfirmCreate = () => {
        setIsConfirmCreateOpen(false);
        void handleSubmitCreateGame();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
            <div className="w-full max-w-115 rounded-md bg-[#F4F2E5] p-5 shadow-[0_6px_20px_rgba(0,0,0,0.35)] pb-12 pt-8">
                <div className="mx-auto w-[256px] rounded-sm border border-[#d6c844] bg-[#f2e44b] py-1 text-center text-xl font-semibold text-[#2B2B2B] shadow-[4px_4px_0_#5d6670]">
                    {title}
                </div>

                <form className="mt-4 space-y-3 text-[10px] text-[#404040]">
                    <label className="block">
                        <span className="text-base font-medium text-[#4E4E4E]">ชื่อ</span>
                        <input
                            type="text"
                            value={exerciseName}
                            onChange={(event) => setExerciseName(event.target.value)}
                            className="mt-1 h-9 w-full rounded-lg border border-[#bfc2c8] bg-white px-2 text-base outline-none cursor-pointer"
                        />
                    </label>

                    <label className="block">
                        <span className="text-base font-medium text-[#4E4E4E]">สร้างโดย</span>
                        <input
                            type="text"
                            value={createBy}
                            onChange={(event) => setCreateBy(event.target.value)}
                            className="mt-1 h-9 w-full rounded-lg border border-[#bfc2c8] bg-white px-2 text-base outline-none cursor-pointer"
                        />
                    </label>

                    <label className="block">
                        <span className="text-base font-medium text-[#4E4E4E]">รายละเอียด</span>
                        <input
                            type="text"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            className="mt-1 h-9 w-full rounded-lg border border-[#bfc2c8] bg-white px-2 text-base outline-none cursor-pointer"
                        />
                    </label>    

                    <label className="block">
                        <span className="text-base font-medium text-[#4E4E4E]">
                            หมวดหมู่
                        </span>
                        <div className="mt-1 relative">
                            <select
                                value={selectedCategoryName}
                                onChange={(event) => setSelectedCategoryName(event.target.value)}
                                className="h-9 w-full rounded-lg border border-[#bfc2c8] bg-white px-2 pr-9 text-base outline-none cursor-pointer appearance-none"
                            >
                                <option value="">None</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.name}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">
                                <MdKeyboardArrowDown size={20} />
                            </span>
                        </div>
                    </label>

                    <label className="block">
                        <span className="text-base font-medium text-[#4E4E4E]">
                            กลุ่ม
                        </span>
                        <div className="mt-1 relative">
                            <select
                                value={selectedGroupName}
                                onChange={(event) => setSelectedGroupName(event.target.value)}
                                className="h-9 w-full rounded-lg border border-[#bfc2c8] bg-white px-2 pr-9 text-base outline-none cursor-pointer appearance-none"
                            >
                                <option value="">None</option>
                                {groups.map((g) => (
                                    <option key={g.id} value={g.name}>
                                        {g.name}
                                    </option>
                                ))}
                            </select>
                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">
                                <MdKeyboardArrowDown size={20} />
                            </span>
                        </div>
                    </label>

                    <label className="block">
                        <span className="text-base font-medium text-[#4E4E4E]">ชั้น</span>
                        <div className="mt-1 relative">
                            <select
                                value={selectedGrade}
                                onChange={(event) => setSelectedGrade(event.target.value)}
                                className="h-9 w-full rounded-lg border border-[#bfc2c8] bg-white px-2 pr-9 text-base outline-none cursor-pointer appearance-none"
                            >
                                <option value="">None</option>
                                <option value="ป.1">ป.1</option>
                                <option value="ป.2">ป.2</option>
                                <option value="ป.3">ป.3</option>
                                <option value="ป.4">ป.4</option>
                                <option value="ป.5">ป.5</option>
                                <option value="ป.6">ป.6</option>
                                <option value="ม.1">ม.1</option>
                                <option value="ม.2">ม.2</option>
                                <option value="ม.3">ม.3</option>
                            </select>
                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">
                                <MdKeyboardArrowDown size={20} />
                            </span>
                        </div>
                    </label>

                    <label className="block">
                        <span className="text-base font-medium text-[#4E4E4E]">
                            ภาพ thumbnail / recommend 480x340
                        </span>
                        <div className="mt-1 relative">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const f = e.target.files && e.target.files[0];
                                    setFileName(f ? f.name : "");
                                    setThumbnailFile(f ?? null);
                                }}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                            <div className="h-9 w-full rounded-lg border border-[#bfc2c8] bg-white px-3 text-base flex items-center justify-between">
                                <span className="text-sm text-[#666] truncate">
                                    {fileName || ""}
                                </span>
                                <FaRegFolder className="text-xl text-[#666]" />
                            </div>
                        </div>
                    </label>

                    <div className="flex justify-center gap-6 pt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="min-w-16 rounded-md border border-[#c4c4c4] bg-white px-6 py-2 text-base font-semibold text-[#444] flex items-center justify-center cursor-pointer"
                        >
                            ยกเลิก
                        </button>
                        <button
                            type="button"
                            onClick={handleClickCreateButton}
                            disabled={isSubmitting}
                            className="min-w-16 rounded-md bg-[#62AC4B] px-7 py-2 text-base font-semibold text-white flex items-center justify-center cursor-pointer"
                        >
                            {isSubmitting ? "กำลังบันทึก..." : "ตกลง"}
                        </button>
                    </div>
                </form>
            </div>

            {isConfirmCreateOpen ? (
                <ConfirmPopUp
                    title="ยืนยันการสร้างเกม"
                    message="ต้องการสร้างเกมนี้หรือไม่"
                    cancelText="ยกเลิก"
                    confirmText="ยืนยัน"
                    onCancel={handleCancelConfirmCreate}
                    onConfirm={handleConfirmCreate}
                    isConfirming={isSubmitting}
                />
            ) : null}

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={handleCloseStatus}
                />
            ) : null}
        </div>
    );
}
